import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';
import { BreadCrumbLink } from 'atoms/Breadcrumbs/index';
import { useSiteConfig } from 'modules/shop/siteConfig/hooks';
import { INavigationItem, Navigation } from 'modules/stroyblok/types';

const findParentCategories = (
  categoryName: string,
  data: INavigationItem[] = [],
  path: BreadCrumbLink[] = [],
): BreadCrumbLink[] => {
  for (const item of data) {
    // multi lang check
    const href = item.url?.story?.full_slug || item.url?.cached_url;
    const newPath = item.title ? [...path, { title: item.title, href }] : path;

    if (item.title === categoryName) {
      return newPath.slice(1); // Exclude the root category
    }

    const children = item.children || [];
    const flyoutItems = item.flyoutColumns?.flatMap((f) => f.items || []) || [];
    const combinedItems = [...children, ...flyoutItems];

    const result = findParentCategories(categoryName, combinedItems, newPath);
    if (result.length) {
      return result;
    }
  }

  return [];
};

export const useProductDetailBreadcrumbRaw = (
  categoryName?: string,
  header?: Navigation,
) => {
  if (!categoryName || !header?.children) {
    return {};
  }

  const categoies = findParentCategories(categoryName, header.children);
  const [current] = categoies.slice(-1);
  return { categoies, current };
};

export const useProductDetailBreadcrumb = (categoryName?: string) => {
  const { header } = useSiteConfig();

  return useProductDetailBreadcrumbRaw(categoryName, header);
};

const findBreadcrumbPath = (
  children: INavigationItem[] = [],
  pathname: string,
  path: BreadCrumbLink[] = [],
): BreadCrumbLink[] | undefined => {
  for (const child of children) {
    // multi lang check
    const href = child.url?.story?.full_slug || child.url?.cached_url;
    const newPath = [...path, { title: child.title, href }];

    /** the cached_url does not have a leading slash,
     *so we need to remove the first slash from the pathname
     * that is why we need  replace method for pathname */
    if (href === pathname.replace('/', '')) {
      return newPath.slice(1); // Remove first level category from path
    }

    const list = child.flyoutColumns?.length
      ? child.flyoutColumns.flatMap((f) => f.items || [])
      : child.children || [];

    const subTreePath = findBreadcrumbPath(list, pathname, newPath);
    if (subTreePath) {
      return subTreePath;
    }
  }
};

export const useCategoryBreadcrumbsRaw = (
  categoryName: string,
  providedPathname?: string,
  header?: Navigation,
) => {
  let pathname = usePathname();

  if (providedPathname) {
    pathname = providedPathname;
  }

  const [categories, setCategories] = useState<BreadCrumbLink[]>([]);

  useEffect(() => {
    const getBreadcrumbs = (): BreadCrumbLink[] => {
      if (!header?.children) {
        return [{ title: categoryName }];
      }
      const breadcrumbPath =
        findBreadcrumbPath(header.children, pathname) || [];
      return breadcrumbPath.length ? breadcrumbPath : [{ title: categoryName }];
    };

    setCategories(getBreadcrumbs());
  }, [categoryName, header?.children, pathname]);
  const [current] = categories.slice(-1);

  return { categories, current };
};

export const useCategoryBreadcrumbs = (
  categoryName: string,
  providedPathname?: string,
) => {
  const { header } = useSiteConfig();

  return useCategoryBreadcrumbsRaw(categoryName, providedPathname, header);
};
